import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { refreshPins } from "../utils/refresh_pins"

// Drag-to-reorder for the arrange-your-home editor, across containers.
//
// Draggable items carry data-pin-id + data-pin-kind ("pin" | "module" |
// "folder" | "widget"). Containers carry data-pin-container: the controller
// element names the outer one, and it may hold further containers — the editor
// splits its pins into "home" and "overflow" either side of the "not on home"
// divider. Each open folder holds a grid keyed by its folder id and marked
// data-pin-folder. A folder card is both a list item and a drop target
// (data-folder-drop=<folder_id>).
//
// Rules:
//  - Folders, modules and widgets reorder among the top-level containers but
//    never drop inside a folder.
//  - Plain pins reorder within any container, can be dropped onto a folder
//    header (append into that folder) or into an empty folder grid.
//  - Only the MIDDLE of a folder's row files into it, and only on release: its
//    top and bottom quarters put the pin above or below it, and hovering the
//    middle just lights it. Filing on hover moved the pin into the (hidden)
//    folder mid-drag, so a pin passing over a folder vanished into it.
//  - In a list of rows the place is read from the pointer's height alone (a
//    chip grid reads both axes).
// On drop we PATCH /pins/reorder with the per-container id lists. Desktop uses
// native HTML5 drag; touch devices get a pointer fallback mirroring the rules.
//
// The same controller drags the rows of the Pins widget on the home itself
// (home_widgets/_pinned, reload-value set): there each save reloads the
// widget's frame, on the folder that was open, so counts and rows stay true.
export default class extends Controller {
    static values = { url: String, reload: Boolean }

    connect() {
        this._items().forEach((el) => this._enable(el))
        this._onDragOverBound = (e) => this._onDragOver(e)
        // A drop inside the list is what makes a lit folder take the pin:
        // released anywhere else (Esc, outside the list) it is not filed.
        this._onDropBound = (e) => { e.preventDefault(); this._dropped = true }
        this.element.addEventListener("dragover", this._onDragOverBound)
        this.element.addEventListener("drop", this._onDropBound)
    }

    disconnect() {
        this.element.removeEventListener("dragover", this._onDragOverBound)
        this.element.removeEventListener("drop", this._onDropBound)
    }

    _items() {
        return Array.from(this.element.querySelectorAll("[data-pin-id]"))
    }

    _enable(el) {
        el.setAttribute("draggable", "true")
        el.addEventListener("dragstart", (e) => this._onDragStart(e, el))
        el.addEventListener("dragend", () => this._onDragEnd())
        el.addEventListener("pointerdown", (e) => this._onPointerDown(e, el))
    }

    // ---- Desktop: native HTML5 drag ----
    _onDragStart(e, el) {
        e.stopPropagation()
        this._dragging = el
        this._draggingKind = el.dataset.pinKind || "pin"
        this._dropped = false
        el.classList.add("is-dragging")
        e.dataTransfer.effectAllowed = "move"
        try { e.dataTransfer.setData("text/plain", el.dataset.pinId) } catch { /* IE guard */ }
    }

    _onDragOver(e) {
        if (!this._dragging) return
        if (this._hover(e.target, e.clientX, e.clientY)) e.preventDefault()
    }

    _onDragEnd() {
        if (!this._dragging) return
        this._dragging.classList.remove("is-dragging")
        // Released over a folder's middle: file it there now (not on hover).
        if (this._intoFolder && this._dropped) this._fileInto(this._intoFolder)
        this._dropped = false
        this._intoFolder = null
        this._clearDropTarget()
        this._dragging = null
        this._draggingKind = null
        this._persist()
    }

    // Shared hover logic for native drag + touch. `target` is the element under
    // the pointer. Returns true when a valid drop position was applied.
    _hover(target, x, y) {
        const dragging = this._dragging
        if (!dragging || !target) return false
        const kind = this._draggingKind
        const overItem = target.closest("[data-pin-id]")
        const folderCard = target.closest("[data-folder-drop]")

        // A pin (or a folder) dropped on the list editor's "‹" (inside a
        // folder's own view) goes up a level - to the folder above, or the top
        // list - at its end.
        const rootDrop = target.closest("[data-pin-drop-root]")
        if (rootDrop && (kind === "pin" || kind === "folder")) {
            const into = rootDrop.dataset.pinDropInto || "root"
            const root = this.element.querySelector(`[data-pin-container="${into}"]`)
            if (root && dragging.parentNode !== root) root.appendChild(dragging)
            this._setDropTarget(rootDrop)
            return true
        }

        // A folder may go one level down (UserPin::MAX_FOLDER_DEPTH): onto a
        // top-level folder's row, or among the pins of one, if it holds no
        // folders itself. The server checks the same.
        const nestable = kind === "folder" && dragging.dataset.holdsFolders !== "true"
        const intoMiddle = folderCard && folderCard !== dragging && (!overItem || overItem === folderCard) && this._inMiddle(folderCard, y)
        if (nestable && intoMiddle && folderCard.dataset.folderDepth === "1") {
            this._intoFolder = folderCard
            this._setDropTarget(folderCard)
            return true
        }
        this._intoFolder = null

        // Folders, modules and widgets: reorder among the top-level containers,
        // and a folder within a top-level folder's list too.
        if (kind !== "pin") {
            if (!overItem || overItem === dragging) return false
            const container = overItem.closest("[data-pin-container]")
            if (!container) return false
            if (container.hasAttribute("data-pin-folder") && !(nestable && container.dataset.containerDepth === "1")) return false
            this._reorderWithin(container, overItem, x, y)
            this._clearDropTarget()
            return true
        }

        // Plain pin over the middle of a folder's row: lit, filed on release.
        if (intoMiddle) {
            this._intoFolder = folderCard
            this._setDropTarget(folderCard)
            return true
        }

        // Plain pin over another item: reorder within that item's container.
        if (overItem && overItem !== dragging) {
            this._reorderWithin(overItem.closest("[data-pin-container]"), overItem, x, y)
            this._clearDropTarget()
            return true
        }

        // Empty container (e.g. an empty folder grid).
        const container = target.closest("[data-pin-container]")
        if (container) {
            if (dragging.parentNode !== container) container.appendChild(dragging)
            this._clearDropTarget()
            return true
        }
        return false
    }

    _reorderWithin(container, overItem, x, y) {
        if (!container || overItem.parentNode !== container) return
        const before = this._isBefore(overItem, x, y)
        container.insertBefore(this._dragging, before ? overItem : overItem.nextSibling)
    }

    _isBefore(el, x, y) {
        const rect = el.getBoundingClientRect()
        // A row spans its list: only the height says above or below. (Reading
        // the width too put a pin above every row whose left half it crossed,
        // so dragging down a list stuck.)
        const container = el.parentNode?.getBoundingClientRect()
        if (container && rect.width > container.width * 0.6) return (y - rect.top) < rect.height / 2
        return (y - rect.top) < rect.height / 2 || (x - rect.left) < rect.width / 2
    }

    // The middle half of a folder's row, top to bottom: the part that files a
    // pin into it rather than beside it.
    _inMiddle(el, y) {
        const rect = el.getBoundingClientRect()
        return y > rect.top + rect.height * 0.25 && y < rect.bottom - rect.height * 0.25
    }

    // Into a folder's list, at its end: a folder chip holds its list, a folder
    // row names it (the list lives in the folder's own view).
    _fileInto(folderCard) {
        const dragging = this._dragging
        if (!dragging) return
        const grid = folderCard.querySelector("[data-pin-container]") ||
            this.element.querySelector(`[data-pin-container="${folderCard.dataset.folderDrop}"]`)
        if (!grid || dragging.parentNode === grid) return
        grid.querySelector(":scope > .home-pinned-empty")?.remove()
        grid.appendChild(dragging)
    }

    _setDropTarget(el) {
        if (this._dropTarget === el) return
        this._clearDropTarget()
        this._dropTarget = el
        el.classList.add("is-drop-target")
    }

    _clearDropTarget() {
        if (this._dropTarget) this._dropTarget.classList.remove("is-drop-target")
        this._dropTarget = null
    }

    // A row's ⋯ "Move to <the level above>": the same move as dropping it on
    // "‹", for when dragging is not how you think of it. The editor reloads on
    // the level it went to.
    moveOut(event) {
        event.preventDefault()
        event.stopPropagation()
        const row = event.target.closest("[data-pin-id]")
        const into = String(event.params.into || "root")
        const list = this.element.querySelector(`[data-pin-container="${into}"]`)
        if (!row || !list) return
        list.appendChild(row)
        this._persist().then(() => refreshPins(this.element, { open: into }))
    }

    // ---- Touch: pointer fallback (coarse pointers only) ----
    _onPointerDown(e, el) {
        if (e.pointerType !== "touch") return
        this._dragging = el
        this._draggingKind = el.dataset.pinKind || "pin"
        this._startX = e.clientX
        this._startY = e.clientY
        this._touchActive = false
        this._touchMove = (ev) => this._onPointerMove(ev)
        this._touchUp = () => this._onPointerUp()
        document.addEventListener("pointermove", this._touchMove, { passive: false })
        document.addEventListener("pointerup", this._touchUp)
        document.addEventListener("pointercancel", this._touchUp)
    }

    _onPointerMove(e) {
        if (!this._dragging) return
        if (!this._touchActive) {
            if (Math.hypot(e.clientX - this._startX, e.clientY - this._startY) < 8) return
            this._touchActive = true
            this._dragging.classList.add("is-dragging")
        }
        e.preventDefault()
        const point = document.elementFromPoint(e.clientX, e.clientY)
        if (point) this._hover(point, e.clientX, e.clientY)
    }

    _onPointerUp() {
        document.removeEventListener("pointermove", this._touchMove, { passive: false })
        document.removeEventListener("pointerup", this._touchUp)
        document.removeEventListener("pointercancel", this._touchUp)
        const wasActive = this._touchActive
        if (this._dragging) this._dragging.classList.remove("is-dragging")
        if (wasActive && this._intoFolder) this._fileInto(this._intoFolder)
        this._intoFolder = null
        this._clearDropTarget()
        this._dragging = null
        this._draggingKind = null
        this._touchActive = false
        if (wasActive) this._persist()
    }

    _persist() {
        const saved = this._save()
        if (this.reloadValue) saved.then(() => this._reloadFrame())
        return saved
    }

    // The widget's frame again, on the folder that was open (pinned_list reads
    // it back off the frame), so a folder's count and rows catch up.
    _reloadFrame() {
        const frame = this.element.closest("turbo-frame")
        if (!frame?.reload) return
        const open = this.element.querySelector("[data-pinned-list-target=folder]:not([hidden])")?.dataset.folderId
        if (open) frame.dataset.openFolder = open
        else delete frame.dataset.openFolder
        frame.reload()
    }

    _save() {
        const containers = {}
        const rootKey = this.element.dataset.pinContainer || "root"
        containers[rootKey] = this._directItemIds(this.element)
        this.element.querySelectorAll("[data-pin-container]").forEach((cont) => {
            containers[cont.dataset.pinContainer] = this._directItemIds(cont)
        })
        return fetch(this.urlValue, {
            method: "PATCH",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ containers })
        }).catch(() => {})
    }

    _directItemIds(container) {
        return Array.from(container.querySelectorAll(":scope > [data-pin-id]"))
            .map((el) => el.dataset.pinId)
            .filter(Boolean)
    }
}
